import { useState, useEffect, useCallback } from 'react';
import {
  UserMetrics,
  UserTableItem,
  UserFilters,
  InviteUserData,
  UserApiResponse,
  UserDetailsResponse,
  UserRole,
} from '../types';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';

export const useUsers = () => {
  const [metrics, setMetrics] = useState<UserMetrics | null>(null);
  const [users, setUsers] = useState<UserTableItem[]>([]);
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await ApiClient.get('/organizations/current/stats/');
      const result = await handleApiResult<UserMetrics>(response, 'Failed to fetch user metrics');

      if (result.success && result.data) {
        setMetrics(result.data);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch user metrics');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch user metrics');
      setError(processedErrors.generalError || 'Failed to fetch user metrics');
    }
  }, []);

  const normalizeRole = (role?: string): UserRole => {
    switch (role?.toLowerCase()) {
      case 'owner':
        return 'owner';
      case 'admin':
        return 'admin';
      case 'manager':
        return 'manager';
      case 'reseller':
        return 'reseller';
      default:
        return 'user';
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiClient.get('/organizations/current/users/');

      if (!response.ok) {
        throw new Error(`Failed to fetch users data: ${response.status}`);
      }

      const result = await handleApiResult<UserApiResponse[]>(response, 'Failed to fetch users');

      if (result.success && result.data) {
        const transformedUsers: UserTableItem[] = result.data.map(user => {
          const userInfo = user.user;
          const firstName = userInfo?.first_name || '—';
          const lastName = userInfo?.last_name || '';
          const email = userInfo?.email || '—';
          const userId = (user.id || userInfo?.id)?.toString() || `user-${Date.now()}`;
          const normalizedRole = normalizeRole(user.role || userInfo?.role);
          const joinedAtRaw = user.joined_at || userInfo?.date_joined || '';
          const joinedAt = joinedAtRaw ? new Date(joinedAtRaw).toLocaleDateString() : '—';

          return {
            id: userId,
            firstName,
            lastName,
            email,
            role: normalizedRole,
            roleDisplay: user.role_display || userInfo?.role || normalizedRole,
            username: userInfo?.username,
            isEmailVerified: Boolean(userInfo?.is_email_verified),
            joinedAt,
            createdAt: joinedAt,
          };
        });
        setUsers(transformedUsers);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch users data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    fetchUsers();
  }, [fetchMetrics, fetchUsers]);

  const inviteUser = async (data: InviteUserData) => {
    const response = await ApiClient.post('/organizations/current/invite-user/', {
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData?.email?.[0] ||
        errorData?.non_field_errors?.[0] ||
        errorData?.detail ||
        'Einladung fehlgeschlagen';
      throw new Error(errorMessage);
    }

    // Refresh data after successful invite
    await Promise.all([fetchMetrics(), fetchUsers()]);
  };

  const fetchUserDetails = async (id: string): Promise<UserDetailsResponse | null> => {
    try {
      const response = await ApiClient.get(`/organizations/current/users/${id}/`);
      const result = await handleApiResult<UserDetailsResponse>(
        response,
        'Failed to fetch user details'
      );

      if (result.success && result.data) {
        return result.data;
      } else {
        setError(result.errors?.generalError || 'Failed to fetch user details');
        return null;
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch user details');
      setError(processedErrors.generalError || 'Failed to fetch user details');
      return null;
    }
  };

  const viewUser = async (_id: string) => {
    // This will be handled by the component to show modal
  };

  const editUser = async (_id: string) => {
    // Navigation to edit user will be implemented
  };

  const deleteUser = async (id: string) => {
    try {
      const response = await ApiClient.delete(`/organizations/current/users/${id}/`);
      if (!response.ok) {
        const processedErrors = await handleApiResult(response, 'Failed to delete user');
        setError(processedErrors.errors?.generalError || 'Failed to delete user');
        return;
      }

      setUsers(prev => prev.filter(user => user.id !== id));
      if (metrics) {
        setMetrics(prev => ({
          ...prev!,
          total_users: prev!.total_users - 1,
        }));
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to delete user');
      setError(processedErrors.generalError || 'Failed to delete user');
    }
  };

  return {
    metrics,
    users,
    filters,
    setFilters,
    loading,
    error,
    inviteUser,
    viewUser,
    editUser,
    deleteUser,
    fetchUserDetails,
  };
};
